/**
 * activity_feed — every vertical. GET /dashboard/activity-feed (api#1147):
 * what happened in the last `days` (the API's), newest first, each line a
 * row's own timestamp and opening that row's screen.
 *
 * FOUR STATES, NEVER COLLAPSED:
 *   - events: the lines, and if some kinds FAILED, a line naming them above —
 *     the list is then a partial one and says so;
 *   - no events, nothing failed: "Nothing in the last N days", a fact;
 *   - no events, something failed: the failure line only, never "nothing",
 *     because the failed kinds may be exactly what happened;
 *   - nothing checked: this role is shown no kind at all, which is a statement
 *     about the role, not about the company's week.
 *
 * `at` is an instant, shown in the tenant's zone the API names (`time_zone`),
 * never the viewer's. `amount` null carries no money and draws nothing; money
 * is one currency per line and is never added up.
 */
import { useNavigate } from 'react-router-dom';
import { WidgetPanel } from '../WidgetPanel';
import { useActivityFeed } from '../overviewQueries';
import { OpsRow, OpsWidgetRead } from '../OpsWidgetRead';
import { moneyVerticalLabel } from '../MoneyVerticalNotes';
import { formatInstant } from '../../../lib/calendar-date';
import { fmtAmount } from '../../../lib/currency-amounts';
import type { ActivityFeed } from '../../../types/overview-activity';

/** The vertical a line belongs to; null is quotes and safety, every company's. */
const sourceLabel = (v: string | null) => (v === null ? 'Quotes & safety' : moneyVerticalLabel(v));

function Body({ d }: { d: ActivityFeed }) {
  const navigate = useNavigate();
  const failedIn = Array.from(new Set(d.failures.map((f) => sourceLabel(f.vertical))));
  return (
    <>
      {d.failures.length > 0 && (
        <p data-testid="af-partial" className="mb-3 rounded-fi-control border border-fieldiq-border px-3 py-2 text-xs text-fieldiq-text">
          Some activity could not be loaded ({failedIn.join(', ')}), so this list may be missing events.
        </p>
      )}
      {d.events.length > 0 ? (
        <div data-testid="af-events" className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
          {d.events.map((e, i) => (
            <OpsRow key={`${e.kind}-${e.at}-${i}`} to={e.link} onOpen={navigate} testId={`af-event-${i}`}>
              <span className="min-w-0">
                <span className="block truncate font-semibold text-fieldiq-text">{e.title}</span>
                <span className="block truncate text-fieldiq-muted">
                  {sourceLabel(e.vertical)} · {formatInstant(e.at, d.time_zone) ?? '—'}
                </span>
              </span>
              {e.amount && <span data-testid="af-amount" className="shrink-0 tabular-nums text-fieldiq-text">{fmtAmount(e.amount)}</span>}
            </OpsRow>
          ))}
        </div>
      ) : d.failures.length > 0 ? null : d.checked.length === 0 ? (
        <p data-testid="af-none-for-role" className="py-6 text-center text-sm text-fieldiq-muted">There is no activity your role can see here.</p>
      ) : (
        <p data-testid="af-none" className="py-6 text-center text-sm text-fieldiq-muted">Nothing in the last {d.days} days.</p>
      )}
    </>
  );
}

export default function ActivityFeedWidget() {
  const q = useActivityFeed();
  const days = q.data?.days;
  return (
    <WidgetPanel widgetKey="activity_feed" eyebrow={days ? `Last ${days} days` : 'Recent'} title="Activity Feed">
      <div className="p-4">
        <OpsWidgetRead q={q} subject="activity" allowed="admins, office staff, supervisors, dispatchers and logistics">
          {(d) => <Body d={d} />}
        </OpsWidgetRead>
      </div>
    </WidgetPanel>
  );
}
